import debugWidget from "./widget-debug.js";
import feedURL from "./feed-url.js";
import loadPreferences from "./load-preferences.js"
import PhotoEvents from "./photo-events.js";

export default Base => class extends Base {
  constructor() {
    super();

    this.initialized = false;
    this.shadow = this.attachShadow({mode: 'closed'});

    this.page = 1;
    this.fetchMorePhotos = this.fetchPhotos.bind(this);
  }

  connectedCallback() {
    if (this.connectedCallbackCalled == true) {
      return;
    }
    this.connectedCallbackCalled = true;

    if (this.shopName) {
      // shopify app blocks
      // ensures dom is loaded before trying to setup header and css templates
      setTimeout(() => { this.setupTemplates() }, 0);
      this.fetchSettings();
    } else {
      this.setupTemplates();
      // if script is loaded before the widget tag is encountered in the html
      if ((typeof L2H === "object") && typeof L2H[this.uuid] === "object") {
        this.setup(L2H[this.uuid].photosURL);
      } else {

      // for when html tag is encountered before preferences for a given user are loaded
        let handler = document.addEventListener("l2h-url-announcement", (event) => {
          let photosURL = event.detail.photosURL;

          if ((this.photos.length == 0) && (photosURL.indexOf(this.uuid) !== -1)) {
            this.setup(photosURL);
            document.removeEventListener("click", handler, false);
          }
        });
      }
    }
  }

  setupTemplates() {
    let headerTemplate = this.querySelector("template#header");
    this.headerHTML = headerTemplate ? headerTemplate.innerHTML : "";

    let cssTemplate = this.querySelector("template#css");
    this.customCSS = cssTemplate ? cssTemplate.innerHTML : "";
  }

  setup(photosURL) {
    this.DEBUG = function(output) {
      debugWidget(output, this.uuid, this);
    }

    this.DEBUG(`received photosURL ${photosURL}`);
    let options = { f: this.feed }
    if (this.utmCampaign && this.utmMedium) {
      options.utm_campaign =  this.utmCampaign;
      options.utm_medium = this.utmMedium;
    }

    this.photosURL = feedURL(photosURL, options)

    this.preferences = loadPreferences(this, L2H[this.uuid].preferences);

    if (this.thumbQuality) {
      this.preferences.useLargeThumbs = true;
    }

    this.initialized = true;

    this.render();
    this.fetchPhotos();
    this.bindPhotoEvents();

    if (this.afterRender) { this.afterRender() }
  }

  // used by Shopify APP Blocks
  fetchSettings() {
    let baseURL = "";

    if (process.env.RAILS_ENV == "development") {
      baseURL = "https://rounders.ngrok.io"
    } else if (process.env.RAILS_ENV == "staging") {
      baseURL = "https://staging.like2have.it"
    } else {
      baseURL = "https://like2have.it"
    }

    let url = new URL(`${baseURL}/preferences.json`);

    url.searchParams.set("shop_name", this.shopName)

    return fetch(url)
      .then(response => response.json())
      .then((data) => {
        if (typeof L2H != "object") {
          window.L2H = {};
        }
        L2H[data.uuid] = data;
        this.uuid = data.uuid
        this.setup(L2H[data.uuid].photosURL);
      });
  }

  bindPhotoEvents() {
    let photoEvents = new PhotoEvents(this.shadow, this.modal, this.photos, this.fetchMorePhotos);
    photoEvents.bindEvents();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.initialized) { return; }

    // if feed has changed, we'll need to reload photos
    if (name === "feed") {
      this.resetPhotos();
    }

    this.preferences = loadPreferences(this, L2H[this.uuid].preferences);
    this.render();

    if (this.afterRender) { this.afterRender() }

    this.bindPhotoEvents();
  }

  resetPhotos() {
    this.photosURL = feedURL(this.photosURL, { f: this.feed })
    this.photos = [];
    this.page = 1;
    this.fetchPhotos();
    this.notFirstFetch = false;
  }

  processPhotos(data) {
    this.appendPhotos(data.photos);
  }

  beforeFetch() {
    this.fetchingPhotos = true;
  }

  afterFetch() {
    this.fetchingPhotos = false;
  }

  fetchPhotos() {
    if (this.fetchingPhotos || this.page === null) { return Promise.resolve() }

    this.beforeFetch();

    let params = { page: this.page , per: this.per };

    if (this.collectionID) {
      params.cf = this.collectionID;
    }

    if (this.productID) {
      params.pf = this.productID;
    }

    if (this.vpg) {
      params.vpg = this.vpg;
    }

    let url = feedURL(this.photosURL, params);

    return fetch(url)
      .then(response => response.json())
      .then((data) => {
        this.photos.push(...data.photos);
        this.page = data.meta.nextPage;
        this.processPhotos(data);

        this.afterFetch();
      })
  }

  renderHeader() {
    const headerHTML = this.photos.length > 0 ? this.headerHTML : ""
    this.shadow.querySelector(".header").innerHTML = headerHTML;
  }

  appendCustomCSS() {
    if (this.customCSS) {
      const style = document.createElement('style');
      style.textContent = this.customCSS;
      this.shadow.appendChild(style);
    }
  }

  integerAttribute(name) {
    let attr = this.getAttribute(name);
    if (attr) {
      return parseInt(attr)
    }
  }

  get settings() {
    return L2H[this.uuid]
  }

  get uuid() {
    return this.getAttribute("uuid");
  }

  set uuid(val) {
    this.setAttribute("uuid", val);
  }

  get feed() {
    return this.getAttribute("feed");
  }

  set photosURL(url) {
    this.setAttribute("photos-url", url);
  }

  get photosURL() {
    return this.getAttribute("photos-url");
  }

  get thumbQuality() {
    return this.getAttribute("uselargethumbs");
  }

  get modal() {
    return this._modal || this.shadow.querySelector("l2h-photo-modal");
  }

  get photoSize() {
    return this.getAttribute("photo-size") || this.DEFAULTS.photoSize;
  }

  get autoScrollDelay() {
    return this.integerAttribute("auto-scroll") ? Math.max(1000, this.integerAttribute("auto-scroll")) : this.DEFAULTS.autoScrollDelay;
  }

  get rows() {
    return this.integerAttribute("rows") || this.DEFAULTS.rows;
  }

  get cols() {
    return this.integerAttribute("cols") || this.DEFAULTS.cols;
  }

  get numPhotos() {
    return this.integerAttribute("num-photos") || this.DEFAULTS.numPhotos;
  }

  get numPhotosTablet() {
    return this.integerAttribute("num-photos-tablet") || this.DEFAULTS.numPhotosTablet;
  }

  get numPhotosPhone() {
    return this.integerAttribute("num-photos-phone") || this.DEFAULTS.numPhotosPhone;
  }

  get linkTarget() {
    return this.getAttribute("link-target") || "_blank";
  }

  get collectionID() {
    return this.getAttribute("collection-id");
  }

  get productID() {
    return this.getAttribute("product-id");
  }

  get vpg() {
    return this.getAttribute("vpg");
  }

  get shopName() {
    return this.getAttribute("shop-name");
  }

  get utmCampaign() {
    return this.getAttribute("utm-campaign")
  }

  get utmMedium() {
    return this.getAttribute("utm-medium")
  }

  get videoAutoplayDisabled() {
    return this.getAttribute("video-autoplay-disabled");
  }

  get videoMuted() {
    return this.getAttribute("video-muted");
  }

  static get observedAttributes() {
    return [
      "feed", "link-target", "utm-campaign", "utm-medium", "collection-id", "photo-size", "photoshopitbordercolor", "photobghovercolor", "photoshopittextcolor",
      "shopitbtntextcolor", "shopitbtnfontsize", "shopitbtnbordersize", "shopitbtnbordercolor", "shopitbtnhovercolor", "shopitbtnborderhovercolor", "shopitbtnbgcolor",
      "shopitbtnbghovercolor", "shopitbtnborderradius", "shopitbtnpaddingtop", "shopitbtnpaddingright", "uselargethumbs", "shopablelinktext", "header", "video-autoplay-disabled", "video-mute"
    ];
  }


}
